import { currentUrl, translate, useMatchMobile } from '@avensia/excite';
import { styled } from '@glitz/react';
import * as React from 'react';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { isSvenssons, Theme } from 'webapp/theme';
import { ButtonLink } from 'webapp/components/button';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { ArrowLeft, ArrowRight } from 'webapp/components/icons';
import { getPaginationUrl, updateUrlWithPaginationSearchParams } from 'webapp/utils/pagination-utils';
import { NnTestIdProps } from 'webapp/shared/test-ids';

import type { JSX } from "react";

type PropType = Excite.ContentApi.PaginationApiModel & {
  onChangePage: () => void;
};

const OPTIONS_COUNT = 3;
const OPTIONS_COUNT_MOBILE = 1;

const ESALES_MAX_ALLOWED_PRODUCT_COUNT = 10000;
const PRODUCT_CARD_PER_PAGE = 36;
const MAX_NUMBER_OF_PAGES = Math.floor(ESALES_MAX_ALLOWED_PRODUCT_COUNT / PRODUCT_CARD_PER_PAGE);

function getOptionButtons(
  currentPage: number,
  totalPageCount: number,
  setPage: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, page: number) => void,
  isMobile: boolean,
) {
  const buttons: JSX.Element[] = [];
  const startIndex = isMobile ? 0 : 1;
  const count = isMobile ? OPTIONS_COUNT_MOBILE : OPTIONS_COUNT;
  for (let i = -startIndex; i < count - startIndex; i++) {
    const page = currentPage + i;
    if (page > 1 && page < totalPageCount) {
      buttons.push(
        <PageButton
          key={page}
          to={getPaginationUrl(page)}
          as={Theme.Transparent}
          onClick={e => setPage(e as React.MouseEvent<HTMLAnchorElement, MouseEvent>, page)}
          square
          disableRippleEffect
          css={styled(textColorDecorator, currentPage === page && currentPageDecorator)}
        >
          {page}
        </PageButton>,
      );
    }
  }

  return buttons;
}

export function Pagination(props: PropType) {
  const { onChangePage } = props;
  const totalPageCount = props.pageCount < MAX_NUMBER_OF_PAGES ? props.pageCount : MAX_NUMBER_OF_PAGES;
  const url = currentUrl();
  const isMobile = useMatchMobile();

  const page = parseInt(url.searchParams.get('p') as string) || 1;
  const [currentPage, setCurrentPage] = React.useState(page);

  React.useEffect(() => {
    setCurrentPage(page);
  }, [page, setCurrentPage]);

  const setPage = React.useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, page: number) => {
      e.preventDefault();
      setCurrentPage(page);
      await updateUrlWithPaginationSearchParams(page);
      onChangePage();
    },
    [setCurrentPage, onChangePage],
  );

  if (totalPageCount <= 1) {
    return null;
  }

  const optionsButtons = getOptionButtons(currentPage, totalPageCount, setPage, isMobile);

  return (
    <Base>
      <PageButtons>
        {currentPage > 1 && (
          <StepButton
            to={getPaginationUrl(currentPage - 1)}
            disableRippleEffect
            square
            as={Theme.Transparent}
            onClick={e => setPage(e as React.MouseEvent<HTMLAnchorElement, MouseEvent>, currentPage - 1)}
          >
            <ArrowLeft />
          </StepButton>
        )}
        <PageButton
          disableRippleEffect
          square
          as={Theme.Transparent}
          to={url.pathname}
          onClick={e => setPage(e as React.MouseEvent<HTMLAnchorElement, MouseEvent>, 1)}
          css={styled(textColorDecorator, 1 === currentPage && currentPageDecorator)}
        >
          1
        </PageButton>
        {currentPage > (isMobile ? 2 : 3) && <Ellipsis>...</Ellipsis>}
        {optionsButtons}
        {totalPageCount > currentPage + (isMobile ? 1 : 2) && <Ellipsis>...</Ellipsis>}
        <PageButton
          disableRippleEffect
          square
          as={Theme.Transparent}
          to={getPaginationUrl(totalPageCount)}
          onClick={e => setPage(e as React.MouseEvent<HTMLAnchorElement, MouseEvent>, totalPageCount)}
          css={styled(textColorDecorator, currentPage === totalPageCount && currentPageDecorator)}
        >
          {totalPageCount}
        </PageButton>
        {currentPage < totalPageCount && (
          <StepButton
            to={getPaginationUrl(currentPage + 1)}
            disableRippleEffect
            square
            as={Theme.Transparent}
            onClick={e => setPage(e as React.MouseEvent<HTMLAnchorElement, MouseEvent>, currentPage + 1)}
            {...NnTestIdProps.ProductListingPaginationNextButton}
          >
            <ArrowRight />
          </StepButton>
        )}
      </PageButtons>
    </Base>
  );
}

const Base = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const PageButtons = styled.div(
  createMinLargeViewportDecorator({
    gap: t => t.sharedSpacing.xs,
  }),
  {
    display: 'flex',
    gap: t => t.sharedSpacing.xxs,
    marginBottom: t => t.sharedSpacing.m,
    maxWidth: '100vw',
  },
);

const currentPageDecorator = styled({
  fontWeight: 'bold',
  color: isSvenssons ? t => t.primary.color : 'inherit',
});

const buttonHoverStyle = styled(
  createButtonHoverDecorator({
    backgroundColor: t => t.secondary.color,
    color: t => t.secondary.text,
    border: { xy: { color: t => t.secondary.color } },
  }),
);

const PageButton = styled(
  ButtonLink,
  createMaxSmallViewportDecorator({
    position: 'relative',
  }),
);

const textColorDecorator = styled({
  color: t => t.facets?.count.color,
});

const StepButton = styled(ButtonLink, {
  cursor: 'pointer',
});

const Ellipsis = styled.div({
  padding: { xy: t => t.sharedSpacing.s },
});
